import {useEffect, useRef, useState} from 'react';

import QrScanner from 'qr-scanner';
import QrFrame from './qr-frame.svg';

import {Box, Button, Center, Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import classes from './QrReader.module.css';

/**
 * Camera-driven QR code scanner: toggle button + live video preview with a
 * scan-target overlay, calling back with the decoded string.
 * @registry Camera QR scanner with start/stop toggle and error states.
 */
export interface QrReaderProps {
  onScanned: (result: string) => void;
  onError: (message: string) => void;
}

export const QrReader: React.FC<QrReaderProps> = ({onScanned, onError}) => {
  const [showCamera, options] = useDisclosure(false);

  // Scanner states
  const scanner = useRef<QrScanner>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [hasCamera, setHasCamera] = useState<boolean | undefined>(undefined);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Success
  // Warning, though. The scanner can read the same result many times!
  const onScanSuccess = (result: QrScanner.ScanResult) => {
    onScanned(result?.data);
  };

  // Fail
  const onScanFail = (err: string | Error) => {
    if (err == 'No QR code found') {
      // This just means we didnt' find a valid QR code. We ignore it.
      return;
    } else {
      console.error(err);
      onError('Error when scanning QR Code. Please contact support.');
    }
  };

  useEffect(() => {
    const fetch = async () => {
      if (showCamera) {
        await initializeCamera();

        if (!scanner?.current) {
          console.warn('Expected camera to be initialized but it is not.');
          return;
        }

        await scanner.current.start().then(
          () => {
            setCameraError(null);
          },
          (error: unknown) => {
            const msg = error instanceof Error ? error.message : String(error);
            const isNotFound =
              /camera not found|no camera/i.test(msg) ||
              /NotFoundError/i.test(msg);
            setCameraError(
              isNotFound
                ? 'No camera detected. Connect a camera to continue.'
                : 'Camera is blocked or not accessible. Please allow camera in your browser permissions and reload the page.'
            );
          }
        );
      } else {
        scanner?.current?.stop();
      }
    };

    void fetch();
  }, [showCamera]);

  const initializeCamera = async () => {
    if (videoRef?.current && !scanner.current) {
      // 👉 Instantiate the QR Scanner
      scanner.current = new QrScanner(videoRef?.current, onScanSuccess, {
        onDecodeError: onScanFail,
        // 📷 This is the camera facing mode. In mobile devices, "environment" means back camera and "user" means front camera.
        preferredCamera: 'environment',
        // 🖼 This will help us position our "QrFrame.svg" so that user can only scan when qr code is put in between our QrFrame.svg.
        highlightScanRegion: true,
        // 🔥 This will produce a yellow (default color) outline around the qr code that we scan, showing a proof that our qr-scanner is scanning that qr code.
        highlightCodeOutline: true,
        // 📦 A custom div which will pair with "highlightScanRegion" option above 👆. This gives us full control over our scan region.
        overlay: frameRef?.current || undefined,
      });

      setHasCamera(await QrScanner.hasCamera());
    } else {
      console.warn(
        'Unable to initialize camera reference. Video Reference is {0} and scanner is {1}',
        videoRef.current,
        scanner.current
      );
    }
  };

  useEffect(() => {
    void initializeCamera();

    return () => {
      scanner?.current?.stop();
    };
  }, []);

  const buttonText = showCamera ? 'Close QR Scanner' : 'Open QR Scanner';

  return (
    <>
      <Center pt={'lg'}>
        <Button
          size="lg"
          color="yellow"
          onClick={options.toggle}
          disabled={hasCamera === false}>
          {buttonText}
        </Button>
      </Center>
      {cameraError && (
        <Center mt="sm">
          <Text c="dimmed" size="sm">
            {cameraError}
          </Text>
        </Center>
      )}
      <Box
        mt="md"
        className={classes.reader}
        style={{display: showCamera && !cameraError ? 'inherit' : 'none'}}>
        <video ref={videoRef}></video>
        <div ref={frameRef} className={classes.qrBox}>
          <img
            src={QrFrame}
            alt="Qr Frame"
            width={256}
            height={256}
            className={classes.qrFrame}
          />
        </div>
      </Box>
    </>
  );
};
